import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    Search, Users, Flame, GraduationCap, BookCheck, Trash2, X, Phone, Mail, MessageSquare, Calendar, Download, Tag, Plus, ArrowUpDown, Film, PlayCircle, Loader2, MessageCircle, Send, RefreshCcw, Bell
} from "lucide-react";
import { useT } from "../i18n";
import { CRM } from "../constants/testIds";
import AnalyticsFunnel from "./AnalyticsFunnel";

const STATUSES = ["hot", "warm", "just_asking", "contact_needed", "unqualified"];

const formatDate = (iso) => {
    try {
        const d = new Date(iso);
        return d.toLocaleDateString("he-IL", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
};

export default function CrmDashboard({ lang, API, token, courses, onUnauthorized }) {
    const T = useT(lang);
    const [leads, setLeads] = useState([]);
    const [stats, setStats] = useState({ total: 0, byStatus: {}, byChannel: {}, avgBagrut: null, avgPsychometric: null });
    const [filterStatus, setFilterStatus] = useState("all");
    const [filterCrmStatus, setFilterCrmStatus] = useState("all");
    const [filterCourse, setFilterCourse] = useState("all");
    const [filterRange, setFilterRange] = useState("all"); // all | today | 7d | 30d | 90d
    const [filterTag, setFilterTag] = useState("all");
    const [sortBy, setSortBy] = useState("date_desc"); // date_desc | date_asc | name_asc | course
    const [search, setSearch] = useState("");
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(true);

    const authHeaders = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

    const [exporting, setExporting] = useState(false);
    const [rescanning, setRescanning] = useState(false);
    const [rescanResult, setRescanResult] = useState(null);

    const rescanConversations = async () => {
        if (rescanning) return;
        const confirmMsg = lang === "ar"
            ? "سنمرّ على كل محادثات الشات (ويب/ماسنجر/واتساب) القديمة ونستخرج أي عملاء محتملين. قد تستغرق العملية دقيقة أو دقيقتين. متابعة؟"
            : "נסרוק את כל שיחות הצ'אט הישנות (אתר/מסנג'ר/וואטסאפ) ונחלץ מהן לידים חדשים. הפעולה עלולה לקחת דקה-שתיים. להמשיך?";
        if (!window.confirm(confirmMsg)) return;
        setRescanning(true);
        setRescanResult(null);
        try {
            const r = await axios.post(`${API}/bot/rescan-conversations`, {}, authHeaders);
            setRescanResult(r.data?.tally || null);
            await loadAll();
        } catch (e) {
            if (e?.response?.status === 401) onUnauthorized && onUnauthorized();
            const errMsg = lang === "ar" ? "فشلت العملية. حاول مرة أخرى." : "הסריקה נכשלה. נסה שוב.";
            alert(errMsg);
        } finally {
            setRescanning(false);
        }
    };
    const exportCsv = async () => {
        if (exporting) return;
        setExporting(true);
        try {
            const r = await axios.get(`${API}/leads/export.csv`, {
                ...authHeaders,
                responseType: "blob",
            });
            const blob = new Blob([r.data], { type: "text/csv;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const today = new Date().toISOString().slice(0, 10);
            a.download = `leads-${today}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            if (e?.response?.status === 401) onUnauthorized && onUnauthorized();
        } finally {
            setExporting(false);
        }
    };

    const loadAll = async () => {
        setLoading(true);
        try {
            const [a, b] = await Promise.all([
                axios.get(`${API}/leads`, authHeaders),
                axios.get(`${API}/leads/stats`, authHeaders),
            ]);
            setLeads(a.data || []);
            setStats(b.data || {});
        } catch (e) {
            if (e?.response?.status === 401) onUnauthorized && onUnauthorized();
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [token]);

    const filteredLeads = useMemo(() => {
        const now = Date.now();
        const dayMs = 24 * 60 * 60 * 1000;
        const rangeMs = { today: dayMs, "7d": 7 * dayMs, "30d": 30 * dayMs, "90d": 90 * dayMs }[filterRange];
        const filtered = leads.filter((l) => {
            if (filterStatus !== "all" && l.interestStatus !== filterStatus) return false;
            if (filterCrmStatus !== "all" && (l.crmStatus || "new") !== filterCrmStatus) return false;
            if (filterCourse !== "all" && l.preferredCourse !== filterCourse) return false;
            if (filterTag !== "all" && !(l.tags || []).includes(filterTag)) return false;
            if (rangeMs) {
                const t = l.dateReceived ? new Date(l.dateReceived).getTime() : 0;
                if (!t || now - t > rangeMs) return false;
            }
            if (search) {
                const q = search.toLowerCase();
                const hay = [l.fullName, l.phone, l.email, l.city, l.notes, ...(l.tags || [])]
                    .filter(Boolean).join(" ").toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
        // Sort
        const sorted = [...filtered];
        if (sortBy === "date_desc") sorted.sort((a, b) => (b.dateReceived || "").localeCompare(a.dateReceived || ""));
        else if (sortBy === "date_asc") sorted.sort((a, b) => (a.dateReceived || "").localeCompare(b.dateReceived || ""));
        else if (sortBy === "name_asc") sorted.sort((a, b) => (a.fullName || "").localeCompare(b.fullName || ""));
        else if (sortBy === "course") sorted.sort((a, b) => (a.preferredCourse || "").localeCompare(b.preferredCourse || ""));
        return sorted;
    }, [leads, filterStatus, filterCourse, filterRange, filterTag, search, sortBy]);

    // All distinct tags across leads, for the filter dropdown
    const allTags = useMemo(() => {
        const s = new Set();
        leads.forEach((l) => (l.tags || []).forEach((t) => t && s.add(t)));
        return Array.from(s).sort();
    }, [leads]);

    const courseName = (cid) => {
        const c = courses.find((x) => x.id === cid);
        return c ? (lang === "ar" ? c.nameAr : c.nameHe) : cid;
    };

    const statusLabel = (s) => ({
        hot: T.statusHot, warm: T.statusWarm, just_asking: T.statusJust,
        unqualified: T.statusUnqualified, contact_needed: T.statusContact,
    }[s] || s);

    // CRM workflow status labels (set by sales reps)
    const CRM_STATUS_META = lang === "ar" ? {
        new:          { label: "جديد",       color: "#0EA5E9", bg: "#DBEAFE" },
        in_progress:  { label: "قيد المعالجة", color: "#D97706", bg: "#FEF3C7" },
        hot:          { label: "ساخن",       color: "#DC2626", bg: "#FEE2E2" },
        closed:       { label: "مغلق",       color: "#16A34A", bg: "#DCFCE7" },
        not_relevant: { label: "غير مناسب",  color: "#64748B", bg: "#F1F5F9" },
    } : {
        new:          { label: "חדש",         color: "#0EA5E9", bg: "#DBEAFE" },
        in_progress:  { label: "בטיפול",      color: "#D97706", bg: "#FEF3C7" },
        hot:          { label: "חם",          color: "#DC2626", bg: "#FEE2E2" },
        closed:       { label: "נסגר",        color: "#16A34A", bg: "#DCFCE7" },
        not_relevant: { label: "לא רלוונטי",  color: "#64748B", bg: "#F1F5F9" },
    };
    const crmStatusMeta = (s) => CRM_STATUS_META[s || "new"] || CRM_STATUS_META.new;

    const updateStatus = async (id, newStatus) => {
        try {
            const r = await axios.put(`${API}/leads/${id}`, { interestStatus: newStatus }, authHeaders);
            setLeads((prev) => prev.map((l) => (l.id === id ? r.data : l)));
            if (selected && selected.id === id) setSelected(r.data);
            loadAll();
        } catch (e) { console.error(e); }
    };

    const updateCrmStatus = async (id, newStatus) => {
        try {
            const r = await axios.put(`${API}/leads/${id}`, { crmStatus: newStatus }, authHeaders);
            setLeads((prev) => prev.map((l) => (l.id === id ? r.data : l)));
            if (selected && selected.id === id) setSelected(r.data);
            loadAll();
        } catch (e) { console.error(e); }
    };

    // Poll for new leads every 20 seconds and pop a toast when the count grows
    const [newLeadToast, setNewLeadToast] = useState(null);
    useEffect(() => {
        if (!token) return;
        let lastKnown = null;
        const iv = setInterval(async () => {
            try {
                const r = await axios.get(`${API}/leads/stats`, authHeaders);
                const cur = r.data?.newCount ?? 0;
                if (lastKnown != null && cur > lastKnown) {
                    setNewLeadToast({ delta: cur - lastKnown, total: cur, at: Date.now() });
                    // Silently refresh the leads list so the new lead appears at the top
                    loadAll();
                }
                lastKnown = cur;
                setStats((prev) => ({ ...prev, ...r.data }));
            } catch (e) { /* ignore */ }
        }, 20000);
        return () => clearInterval(iv);
    }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-dismiss the toast after 8 seconds
    useEffect(() => {
        if (!newLeadToast) return;
        const t = setTimeout(() => setNewLeadToast(null), 8000);
        return () => clearTimeout(t);
    }, [newLeadToast]);

    const updateTags = async (id, newTags) => {
        try {
            const r = await axios.put(`${API}/leads/${id}`, { tags: newTags }, authHeaders);
            setLeads((prev) => prev.map((l) => (l.id === id ? r.data : l)));
            if (selected && selected.id === id) setSelected(r.data);
        } catch (e) { console.error(e); }
    };

    const deleteLead = async (id) => {
        if (!window.confirm(T.confirmDelete)) return;
        try {
            await axios.delete(`${API}/leads/${id}`, authHeaders);
            setLeads((prev) => prev.filter((l) => l.id !== id));
            setSelected(null);
            loadAll();
        } catch (e) { console.error(e); }
    };

    return (
        <div className="fade-up" data-testid="crm-dashboard">
            <div className="mb-6">
                <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] gold-underline inline-block">
                    {T.crmTitle}
                </h2>
                <p className="text-[#475569] mt-3">{T.crmSubtitle}</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <StatCard testId={CRM.statsTotal} icon={Users} value={stats.total ?? 0} label={T.crmTotal} accent="#1A365D" />
                <StatCard
                    testId="crm-stats-new"
                    icon={Bell}
                    value={stats.newCount ?? 0}
                    label={lang === "ar" ? "🔔 عملاء جدد" : "🔔 לידים חדשים"}
                    accent="#0EA5E9"
                />
                <StatCard testId={CRM.statsHot} icon={Flame} value={stats.byStatus?.hot ?? 0} label={T.crmHot} accent="#EF4444" />
                <StatCard testId={CRM.statsAvgBagrut} icon={GraduationCap} value={stats.avgBagrut ?? "—"} label={T.crmAvgBagrut} accent="#D4AF37" />
                <StatCard testId={CRM.statsAvgPsych} icon={BookCheck} value={stats.avgPsychometric ?? "—"} label={T.crmAvgPsych} accent="#22C55E" />
            </div>

            {/* New-lead toast (auto-dismisses after 8 sec) */}
            {newLeadToast && (
                <div
                    className="fixed bottom-6 inset-x-6 md:inset-auto md:end-6 md:bottom-6 z-[60] bg-[#0B1F3D] text-white px-5 py-4 rounded-xl shadow-2xl border-s-4 border-[#D4761F] flex items-start gap-3 max-w-md animate-[pwSlide_.4s_ease-out]"
                    data-testid="new-lead-toast"
                >
                    <Bell size={20} className="text-[#F59E0B] shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <div className="font-bold text-sm mb-1">
                            {lang === "ar"
                                ? `🎯 ${newLeadToast.delta} عميل جديد وصل`
                                : `🎯 ${newLeadToast.delta} ליד חדש נכנס`}
                        </div>
                        <div className="text-xs text-[#CBD5E1]">
                            {lang === "ar"
                                ? `المجموع في الانتظار: ${newLeadToast.total}. ظهر في أعلى القائمة.`
                                : `סה״כ ממתינים: ${newLeadToast.total}. הופיע בראש הרשימה.`}
                        </div>
                    </div>
                    <button
                        onClick={() => setNewLeadToast(null)}
                        className="text-[#94A3B8] hover:text-white transition"
                    >
                        <X size={16} />
                    </button>
                </div>
            )}

            {/* Funnel Analytics (per channel) */}
            <AnalyticsFunnel
                lang={lang}
                API={API}
                token={token}
                refreshKey={`${leads.length}-${rescanResult?.created || 0}`}
            />

            {/* Filters */}
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 mb-4 flex flex-wrap gap-3 items-center">
                <div className="flex items-center gap-2 flex-1 min-w-[220px]">
                    <Search size={18} color="#475569" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={lang === "ar" ? "بحث بالاسم/الهاتف/البلدة/التاجات..." : "חיפוש בשם/טלפון/עיר/תגית..."}
                        className="input-royal !py-1.5 flex-1"
                        data-testid={CRM.searchInput}
                    />
                </div>
                <select
                    value={filterRange}
                    onChange={(e) => setFilterRange(e.target.value)}
                    className="input-royal !py-1.5 !w-auto"
                    data-testid="crm-filter-range"
                >
                    <option value="all">{lang === "ar" ? "كل الأوقات" : "כל הזמנים"}</option>
                    <option value="today">{lang === "ar" ? "اليوم" : "היום"}</option>
                    <option value="7d">{lang === "ar" ? "آخر 7 أيام" : "7 ימים אחרונים"}</option>
                    <option value="30d">{lang === "ar" ? "آخر 30 يوماً" : "30 ימים אחרונים"}</option>
                    <option value="90d">{lang === "ar" ? "آخر 90 يوماً" : "90 ימים אחרונים"}</option>
                </select>
                <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="input-royal !py-1.5 !w-auto"
                    data-testid={CRM.filterStatus}
                >
                    <option value="all">{T.all} ({T.status})</option>
                    {STATUSES.map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
                </select>
                <select
                    value={filterCrmStatus}
                    onChange={(e) => setFilterCrmStatus(e.target.value)}
                    className="input-royal !py-1.5 !w-auto"
                    data-testid="crm-filter-crm-status"
                >
                    <option value="all">{lang === "ar" ? "الكل (حالة CRM)" : "הכל (סטטוס CRM)"}</option>
                    <option value="new">{CRM_STATUS_META.new.label}</option>
                    <option value="in_progress">{CRM_STATUS_META.in_progress.label}</option>
                    <option value="hot">{CRM_STATUS_META.hot.label}</option>
                    <option value="closed">{CRM_STATUS_META.closed.label}</option>
                    <option value="not_relevant">{CRM_STATUS_META.not_relevant.label}</option>
                </select>
                <select
                    value={filterCourse}
                    onChange={(e) => setFilterCourse(e.target.value)}
                    className="input-royal !py-1.5 !w-auto"
                    data-testid={CRM.filterCourse}
                >
                    <option value="all">{T.all} ({T.course})</option>
                    {courses.map((c) => <option key={c.id} value={c.id}>{lang === "ar" ? c.nameAr : c.nameHe}</option>)}
                </select>
                {allTags.length > 0 && (
                    <select
                        value={filterTag}
                        onChange={(e) => setFilterTag(e.target.value)}
                        className="input-royal !py-1.5 !w-auto"
                        data-testid="crm-filter-tag"
                    >
                        <option value="all">{lang === "ar" ? "كل التاجات" : "כל התגיות"}</option>
                        {allTags.map((t) => <option key={t} value={t}>#{t}</option>)}
                    </select>
                )}
                <div className="flex items-center gap-1.5">
                    <ArrowUpDown size={14} color="#475569" />
                    <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="input-royal !py-1.5 !w-auto"
                        data-testid="crm-sort-by"
                    >
                        <option value="date_desc">{lang === "ar" ? "الأحدث أولاً" : "חדש → ישן"}</option>
                        <option value="date_asc">{lang === "ar" ? "الأقدم أولاً" : "ישן → חדש"}</option>
                        <option value="name_asc">{lang === "ar" ? "الاسم أ→ي" : "שם א→ת"}</option>
                        <option value="course">{lang === "ar" ? "المسار" : "מסלול"}</option>
                    </select>
                </div>
                <div className="text-xs text-[#475569] px-2 py-1 bg-[#F1F5F9] rounded">
                    {filteredLeads.length} / {leads.length}
                </div>
                <button
                    onClick={exportCsv}
                    disabled={exporting || loading || leads.length === 0}
                    className="btn-gold !py-1.5 !px-3 !text-xs disabled:opacity-50"
                    title={lang === "ar" ? "تصدير إلى Excel/CSV" : "ייצוא ל-Excel/CSV"}
                    data-testid="crm-export-csv"
                >
                    <Download size={14} />
                    {exporting
                        ? (lang === "ar" ? "جارٍ التصدير..." : "מייצא...")
                        : (lang === "ar" ? "تصدير CSV" : "ייצוא CSV")}
                </button>
                <button
                    onClick={rescanConversations}
                    disabled={rescanning || loading}
                    className="btn-outline !py-1.5 !px-3 !text-xs disabled:opacity-50"
                    title={lang === "ar" ? "استخراج عملاء محتملين من المحادثات القديمة" : "חילוץ לידים משיחות ישנות"}
                    data-testid="crm-rescan-conversations"
                >
                    {rescanning ? <Loader2 size={14} className="animate-spin" /> : <RefreshCcw size={14} />}
                    {rescanning
                        ? (lang === "ar" ? "جارٍ الفحص..." : "סורק...")
                        : (lang === "ar" ? "استخراج من المحادثات" : "חילוץ משיחות ישנות")}
                </button>
            </div>

            {rescanResult && (
                <div className="text-xs bg-[#F0FDF4] border border-[#BBF7D0] text-[#166534] px-3 py-2 rounded-lg" data-testid="crm-rescan-result">
                    {lang === "ar"
                        ? `تم فحص ${rescanResult.scanned} محادثة — تم إنشاء ${rescanResult.created} عميل جديد وتحديث ${rescanResult.updated}.`
                        : `נסרקו ${rescanResult.scanned} שיחות — נוצרו ${rescanResult.created} לידים חדשים ועודכנו ${rescanResult.updated}.`}
                </div>
            )}

            {/* Table */}
            <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-[#F8F9FA] border-b border-[#E2E8F0]">
                            <tr>
                                <th className="px-4 py-3 text-right font-semibold text-[#1A365D]">{T.name}</th>
                                <th className="px-4 py-3 text-right font-semibold text-[#1A365D]">{T.phone}</th>
                                <th className="px-4 py-3 text-right font-semibold text-[#1A365D]">{T.course}</th>
                                <th className="px-4 py-3 text-right font-semibold text-[#1A365D]">{T.bagrut}</th>
                                <th className="px-4 py-3 text-right font-semibold text-[#1A365D]">{T.psych}</th>
                                <th className="px-4 py-3 text-right font-semibold text-[#1A365D]">{T.status}</th>
                                <th className="px-4 py-3 text-right font-semibold text-[#1A365D]">
                                    {lang === "ar" ? "حالة CRM" : "סטטוס CRM"}
                                </th>
                                <th className="px-4 py-3 text-right font-semibold text-[#1A365D]">{T.received}</th>
                                <th className="px-4 py-3 text-right font-semibold text-[#1A365D]">{T.actions}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr><td colSpan={8} className="text-center py-10 text-[#475569]">...</td></tr>
                            )}
                            {!loading && filteredLeads.length === 0 && (
                                <tr><td colSpan={8} className="text-center py-12 text-[#475569]">{T.noLeads}</td></tr>
                            )}
                            {filteredLeads.map((l) => (
                                <tr
                                    key={l.id}
                                    onClick={() => setSelected(l)}
                                    className="border-b border-[#F1F5F9] hover:bg-[#F8F9FA] cursor-pointer"
                                    data-testid={CRM.leadRow(l.id)}
                                >
                                    <td className="px-4 py-3 font-medium text-[#0F172A]">
                                        <div>{l.fullName}</div>
                                        {(l.tags && l.tags.length > 0) && (
                                            <div className="flex flex-wrap gap-1 mt-1">
                                                {l.tags.slice(0, 3).map((t) => (
                                                    <span key={t} className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-[#D4AF37]/10 text-[#8B6F1E] border border-[#D4AF37]/30">
                                                        <Tag size={8} />{t}
                                                    </span>
                                                ))}
                                                {l.tags.length > 3 && (
                                                    <span className="text-[10px] text-[#475569]">+{l.tags.length - 3}</span>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-[#475569]" dir="ltr">{l.phone}</td>
                                    <td className="px-4 py-3 text-[#475569]">{courseName(l.preferredCourse)}</td>
                                    <td className="px-4 py-3 text-[#475569]">{l.highSchoolAverage ?? "—"}</td>
                                    <td className="px-4 py-3 text-[#475569]">{l.psychometricScore ?? "—"}</td>
                                    <td className="px-4 py-3">
                                        <span className={`status-badge badge-${l.interestStatus}`}>{statusLabel(l.interestStatus)}</span>
                                    </td>
                                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                                        <select
                                            value={l.crmStatus || "new"}
                                            onChange={(e) => updateCrmStatus(l.id, e.target.value)}
                                            className="text-xs font-semibold px-2 py-1 rounded border-0 outline-0 cursor-pointer"
                                            style={{
                                                backgroundColor: crmStatusMeta(l.crmStatus).bg,
                                                color: crmStatusMeta(l.crmStatus).color,
                                            }}
                                            data-testid={`crm-status-select-${l.id}`}
                                        >
                                            <option value="new">{CRM_STATUS_META.new.label}</option>
                                            <option value="in_progress">{CRM_STATUS_META.in_progress.label}</option>
                                            <option value="hot">{CRM_STATUS_META.hot.label}</option>
                                            <option value="closed">{CRM_STATUS_META.closed.label}</option>
                                            <option value="not_relevant">{CRM_STATUS_META.not_relevant.label}</option>
                                        </select>
                                    </td>
                                    <td className="px-4 py-3 text-[#475569] text-xs">{formatDate(l.dateReceived)}</td>
                                    <td className="px-4 py-3">
                                        <button
                                            onClick={(e) => { e.stopPropagation(); deleteLead(l.id); }}
                                            className="p-1.5 rounded hover:bg-red-50 text-red-600"
                                            title={T.delete}
                                            data-testid={`crm-delete-${l.id}`}
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Lead detail drawer */}
            {selected && (
                <div className="fixed inset-0 z-50 flex" onClick={() => setSelected(null)}>
                    <div className="absolute inset-0 bg-[#0F172A]/40 backdrop-blur-sm" />
                    <aside
                        onClick={(e) => e.stopPropagation()}
                        className={`relative ml-auto bg-white w-full max-w-md h-full shadow-2xl overflow-y-auto fade-up`}
                        style={{ marginLeft: 0, marginRight: "auto" }}
                        data-testid={CRM.leadDetail}
                    >
                        <div className="bg-[#1A365D] text-white p-5 sticky top-0 z-10 flex items-start justify-between">
                            <div>
                                <h3 className="font-serif text-xl font-bold">{selected.fullName}</h3>
                                <p className="text-[#D4AF37] text-xs mt-0.5">{T.leadDetails}</p>
                            </div>
                            <button onClick={() => setSelected(null)} className="text-white/80 hover:text-white" data-testid={CRM.closeDetailBtn}>
                                <X size={22} />
                            </button>
                        </div>
                        <div className="p-5 space-y-4">
                            <InfoRow icon={Phone} label={T.phone} value={selected.phone} ltr />
                            {selected.email && <InfoRow icon={Mail} label={T.email} value={selected.email} ltr />}
                            <InfoRow icon={GraduationCap} label={T.course} value={courseName(selected.preferredCourse)} />
                            <div className="grid grid-cols-2 gap-3">
                                <InfoRow icon={BookCheck} label={T.bagrut} value={selected.highSchoolAverage ?? "—"} />
                                <InfoRow icon={BookCheck} label={T.psych} value={selected.psychometricScore ?? "—"} />
                            </div>
                            <InfoRow icon={Calendar} label={T.received} value={formatDate(selected.dateReceived)} />

                            <div>
                                <label className="text-xs uppercase tracking-wider font-bold text-[#1A365D] block mb-1.5">{T.status}</label>
                                <select
                                    value={selected.interestStatus}
                                    onChange={(e) => updateStatus(selected.id, e.target.value)}
                                    className="input-royal"
                                    data-testid={CRM.updateStatusSelect}
                                >
                                    {STATUSES.map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
                                </select>
                            </div>

                            <TagsEditor
                                lang={lang}
                                allTags={allTags}
                                tags={selected.tags || []}
                                onChange={(next) => updateTags(selected.id, next)}
                            />

                            <WelcomeVideoBlock
                                lang={lang}
                                API={API}
                                authHeaders={authHeaders}
                                lead={selected}
                                onUpdate={(patch) => {
                                    setLeads((prev) => prev.map((l) => l.id === selected.id ? { ...l, ...patch } : l));
                                    setSelected((cur) => cur ? { ...cur, ...patch } : cur);
                                }}
                            />

                            <WhatsAppSendBlock
                                lang={lang}
                                API={API}
                                authHeaders={authHeaders}
                                lead={selected}
                            />

                            <MessengerReplyBlock
                                API={API}
                                authHeaders={authHeaders}
                                lead={selected}
                                onSent={loadLeads}
                            />

                            <div>
                                <div className="flex items-center gap-2 mb-2">
                                    <MessageSquare size={16} color="#1A365D" />
                                    <h4 className="font-bold text-[#1A365D]">{T.conversation}</h4>
                                </div>
                                {(!selected.conversationLog || selected.conversationLog.length === 0) ? (
                                    <p className="text-sm text-[#475569]">{T.noConversation}</p>
                                ) : (
                                    <div className="space-y-2 max-h-96 overflow-y-auto bg-[#FAFAFA] p-3 rounded-lg border border-[#E2E8F0]">
                                        {selected.conversationLog.map((m, i) => (
                                            <div key={i} className={`flex ${m.sender === "user" ? "justify-start" : "justify-end"}`}>
                                                <div className={`text-xs px-3 py-2 rounded-lg max-w-[85%] ${m.sender === "user" ? "bubble-user" : "bubble-bot"}`}>
                                                    {m.text}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                        </div>
                    </aside>
                </div>
            )}
        </div>
    );
}

function MessengerReplyBlock({ API, authHeaders, lead, onSent }) {
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [sentOk, setSentOk] = useState(false);
    const [error, setError] = useState("");

    const psid = lead?.psid || lead?.messengerPsid;
    const isMessengerLead = (lead?.rawInputChannel || "").toLowerCase().includes("messenger") || !!psid;
    if (!isMessengerLead) return null;

    const send = async () => {
        if (!text.trim()) return;
        setSending(true);
        setError("");
        setSentOk(false);
        try {
            await axios.post(`${API}/api/leads/${lead.id}/messenger-reply`,
                { text: text.trim() },
                { headers: authHeaders });
            setSentOk(true);
            setText("");
            if (onSent) onSent();
            setTimeout(() => setSentOk(false), 4000);
        } catch (err) {
            setError(err?.response?.data?.detail || "Failed to send reply");
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="bg-[#EFF6FF] border border-[#1877F2] rounded-lg p-3" data-testid="messenger-reply-block">
            <div className="flex items-center gap-2 mb-2">
                <MessageCircle size={16} color="#1877F2" />
                <h4 className="font-bold text-[#1877F2]" data-testid="messenger-reply-title">Reply on Facebook Messenger</h4>
            </div>
            <p className="text-xs text-[#475569] mb-2">
                Type a message and it will be sent to the student's Facebook Messenger conversation via the Page Send API.
            </p>
            <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Type your reply here…"
                rows={3}
                data-testid="messenger-reply-textarea"
                className="w-full text-sm border border-[#CBD5E1] rounded p-2 focus:border-[#1877F2] focus:outline-none"
                dir="auto"
            />
            <div className="flex items-center gap-2 mt-2">
                <button
                    onClick={send}
                    disabled={sending || !text.trim()}
                    data-testid="messenger-reply-send-btn"
                    className="flex items-center gap-1 px-3 py-1.5 bg-[#1877F2] text-white text-sm rounded font-semibold disabled:opacity-50 hover:bg-[#0866FF]"
                >
                    {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                    {sending ? "Sending…" : "Send to Messenger"}
                </button>
                {sentOk && <span className="text-xs text-[#10B981] font-semibold" data-testid="messenger-reply-success">✓ Sent — appears in Messenger</span>}
                {error && <span className="text-xs text-[#EF4444] font-semibold" data-testid="messenger-reply-error">{error}</span>}
            </div>
        </div>
    );
}


function StatCard({ icon: Icon, value, label, accent, testId }) {
    return (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 card-elevate" data-testid={testId}>
            <div className="flex items-center justify-between mb-2">
                <Icon size={22} color={accent} />
            </div>
            <div className="font-serif text-3xl font-bold text-[#1A365D]">{value}</div>
            <div className="text-xs uppercase tracking-wider text-[#475569] font-semibold mt-1">{label}</div>
        </div>
    );
}

function InfoRow({ icon: Icon, label, value, ltr }) {
    return (
        <div className="flex items-start gap-3">
            <Icon size={16} color="#D4AF37" className="mt-1" />
            <div className="flex-1">
                <div className="text-[10px] uppercase tracking-wider text-[#1A365D] font-bold">{label}</div>
                <div className="text-sm text-[#0F172A]" dir={ltr ? "ltr" : "rtl"}>{value}</div>
            </div>
        </div>
    );
}

const SUGGESTED_TAGS_HE = ["דחוף", "מועדף", "המתנה", "לחזור אליו", "וואטסאפ נשלח", "הורה מלווה", "רלוונטי-2027"];

function WhatsAppSendBlock({ lang, API, authHeaders, lead }) {
    const isAr = lang === "ar";
    const [status, setStatus] = React.useState(null);
    const [open, setOpen] = React.useState(false);
    const [text, setText] = React.useState("");
    const [sending, setSending] = React.useState(false);
    const [result, setResult] = React.useState(null);

    React.useEffect(() => {
        let cancelled = false;
        axios.get(`${API}/whatsapp/status`, authHeaders)
            .then((r) => { if (!cancelled) setStatus(r.data); })
            .catch(() => { if (!cancelled) setStatus({ configured: false }); });
        return () => { cancelled = true; };
    }, [API, authHeaders]);

    // Default greeting
    React.useEffect(() => {
        const first = (lead.fullName || "").trim().split(" ")[0] || "";
        const dflt = isAr
            ? `مرحبا ${first}! هذه رسالة من الكلية الأهلية. سعدنا بتواصلك معنا 🌟`
            : `שלום ${first}! זו הודעה מהמכללה האהלייה. שמחנו על יצירת הקשר 🌟`;
        setText(dflt);
        setResult(null);
    }, [lead.id, isAr, lead.fullName]);

    const send = async () => {
        if (sending || !text.trim() || !lead.phone) return;
        setSending(true);
        setResult(null);
        try {
            const r = await axios.post(`${API}/whatsapp/send-text`, {
                to: lead.phone,
                body: text.trim(),
            }, authHeaders);
            setResult({ ok: r.data?.ok !== false, message: r.data?.ok !== false ? (isAr ? "تم الإرسال ✓" : "נשלח ✓") : (r.data?.error || "") });
        } catch (e) {
            const detail = e?.response?.data?.detail || e?.message || "error";
            setResult({ ok: false, message: detail });
        } finally {
            setSending(false);
        }
    };

    if (!status) return null;

    const configured = status.configured;

    return (
        <div className="rounded-xl border border-green-500/40 bg-gradient-to-br from-green-50 to-white p-3" data-testid="crm-wa-block">
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                    <MessageCircle size={16} color="#16A34A" />
                    <h4 className="font-bold text-[#1A365D] text-sm">
                        {isAr ? "إرسال رسالة WhatsApp" : "שלח הודעת WhatsApp"}
                    </h4>
                </div>
                {!configured && (
                    <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300">
                        {isAr ? "غير مُعد" : "לא מוגדר"}
                    </span>
                )}
            </div>

            {!configured ? (
                <p className="text-[11px] text-[#475569] leading-relaxed">
                    {isAr
                        ? "لتشغيل الإرسال عبر WhatsApp، يجب إعداد رقم WhatsApp Business في لوحة Meta وإضافة الرمز في متغيرات البيئة."
                        : "כדי להפעיל שליחה בוואטסאפ, יש להגדיר מספר WhatsApp Business ב-Meta ולהוסיף את הטוקן במשתני הסביבה."}
                </p>
            ) : (
                <>
                    <div className="text-[11px] text-[#475569] mb-1.5" dir="ltr">
                        <Phone size={10} className="inline me-1" />{lead.phone}
                    </div>
                    {!open ? (
                        <button
                            onClick={() => setOpen(true)}
                            className="btn-royal w-full !py-2 !text-sm bg-green-600 hover:bg-green-700"
                            data-testid="crm-wa-open-composer"
                        >
                            <MessageCircle size={14} />
                            {isAr ? "افتح المُحرر" : "פתח כתיבה"}
                        </button>
                    ) : (
                        <>
                            <textarea
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                rows={3}
                                className="input-royal w-full text-sm"
                                placeholder={isAr ? "اكتب رسالتك..." : "כתוב את ההודעה..."}
                                data-testid="crm-wa-textarea"
                            />
                            <div className="flex items-center gap-2 mt-2">
                                <button
                                    onClick={send}
                                    disabled={sending || !text.trim()}
                                    className="btn-royal !py-1.5 !text-xs bg-green-600 hover:bg-green-700 disabled:opacity-50"
                                    data-testid="crm-wa-send-btn"
                                >
                                    {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                                    {isAr ? "إرسال" : "שלח"}
                                </button>
                                <button
                                    onClick={() => setOpen(false)}
                                    className="text-xs text-[#475569] hover:text-red-600"
                                >
                                    {isAr ? "إلغاء" : "ביטול"}
                                </button>
                                {result && (
                                    <span className={`text-[11px] ${result.ok ? "text-green-700" : "text-red-600"}`}>
                                        {result.message}
                                    </span>
                                )}
                            </div>
                            <p className="text-[9px] text-[#94A3B8] mt-1">
                                {isAr
                                    ? "الرسائل الحرة تعمل خلال 24 ساعة من آخر رد للعميل. خارج ذلك — قوالب معتمدة فقط."
                                    : "הודעות חופשיות פועלות ב-24 שעות מאז תגובה אחרונה. מחוץ לזה — רק תבניות מאושרות."}
                            </p>
                        </>
                    )}
                </>
            )}
        </div>
    );
}


function WelcomeVideoBlock({ lang, API, authHeaders, lead, onUpdate }) {
    const isAr = lang === "ar";
    const [busy, setBusy] = React.useState(false);
    const [status, setStatus] = React.useState(lead.welcomeVideoStatus || null);
    const [videoUrl, setVideoUrl] = React.useState(lead.welcomeVideoUrl || null);
    const pollTimer = React.useRef(null);

    // Sync when a different lead is selected
    React.useEffect(() => {
        setStatus(lead.welcomeVideoStatus || null);
        setVideoUrl(lead.welcomeVideoUrl || null);
        if (pollTimer.current) { clearTimeout(pollTimer.current); pollTimer.current = null; }
    }, [lead.id, lead.welcomeVideoStatus, lead.welcomeVideoUrl]);

    // Poll HeyGen while processing
    React.useEffect(() => {
        if (!lead.welcomeVideoId) return;
        if (videoUrl) return; // already have it
        if (status === "completed" || status === "failed") return;
        let cancelled = false;
        const tick = async () => {
            try {
                const r = await axios.get(`${API}/leads/${lead.id}/welcome-video-status`, authHeaders);
                if (cancelled) return;
                const s = r.data?.status;
                const url = r.data?.video_url;
                setStatus(s || status);
                if (url) {
                    setVideoUrl(url);
                    onUpdate && onUpdate({ welcomeVideoUrl: url, welcomeVideoStatus: "completed" });
                    return; // stop polling
                }
                if (s === "failed") return;
                pollTimer.current = setTimeout(tick, 10_000);
            } catch (_e) {
                if (!cancelled) pollTimer.current = setTimeout(tick, 15_000);
            }
        };
        pollTimer.current = setTimeout(tick, 3_000);
        return () => {
            cancelled = true;
            if (pollTimer.current) clearTimeout(pollTimer.current);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lead.id, lead.welcomeVideoId, status]);

    const generate = async () => {
        if (busy) return;
        setBusy(true);
        try {
            const r = await axios.post(`${API}/leads/${lead.id}/generate-welcome-video`, {}, authHeaders);
            const vid = r.data?.video_id;
            const st = r.data?.status || "processing";
            setStatus(st);
            setVideoUrl(null);
            onUpdate && onUpdate({ welcomeVideoId: vid, welcomeVideoStatus: st, welcomeVideoUrl: null });
        } catch (e) {
            alert(isAr ? "فشل الإنشاء. تحقق من رصيد HeyGen." : "יצירה נכשלה. בדוק יתרת HeyGen.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="rounded-xl border border-[#D4AF37]/40 bg-gradient-to-br from-[#FFFBEA] to-white p-3" data-testid="crm-welcome-video-block">
            <div className="flex items-center gap-2 mb-2">
                <Film size={16} color="#B36318" />
                <h4 className="font-bold text-[#1A365D] text-sm">
                    {isAr ? "فيديو ترحيبي شخصي من نور 🎬" : "וידאו ברכה אישי מנור 🎬"}
                </h4>
            </div>

            {videoUrl ? (
                <>
                    <video src={videoUrl} controls playsInline className="w-full rounded-lg bg-black" data-testid="crm-welcome-video-player" />
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <a
                            href={videoUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[11px] text-[#8B6F1E] underline"
                            data-testid="crm-welcome-video-link"
                        >
                            {isAr ? "فتح في نافذة جديدة" : "פתח בחלון חדש"}
                        </a>
                        <button
                            onClick={() => navigator.clipboard?.writeText(videoUrl)}
                            className="text-[11px] text-[#8B6F1E] underline"
                            data-testid="crm-welcome-video-copy"
                        >
                            {isAr ? "نسخ الرابط" : "העתק קישור"}
                        </button>
                        <button
                            onClick={generate}
                            disabled={busy}
                            className="text-[11px] text-[#475569] underline ms-auto disabled:opacity-40"
                            data-testid="crm-welcome-video-regenerate"
                        >
                            {isAr ? "إنشاء نسخة جديدة" : "צור גרסה חדשה"}
                        </button>
                    </div>
                </>
            ) : status === "processing" || status === "waiting" ? (
                <div className="flex items-center gap-2 text-[13px] text-[#B36318] py-3">
                    <Loader2 size={16} className="animate-spin" />
                    {isAr ? "جارٍ إنشاء الفيديو الشخصي... (1-3 دقائق)" : "יוצר את הוידאו האישי... (1-3 דקות)"}
                </div>
            ) : status === "failed" ? (
                <div className="text-[13px] text-red-600 mb-2">
                    {isAr ? "فشل الإنشاء. حاول مرة أخرى." : "היצירה נכשלה. נסה שוב."}
                </div>
            ) : (
                <p className="text-[11px] text-[#475569] mb-2">
                    {isAr
                        ? "نور تُسجّل رسالة ترحيبية بصوتها الحقيقي، تذكر اسم المتقدم والمسار الذي اختاره."
                        : "נור מקליטה הודעת ברכה בקולה האמיתי, מזכירה את שם המועמד והמסלול שבחר."}
                </p>
            )}

            {!videoUrl && !(status === "processing" || status === "waiting") && (
                <button
                    onClick={generate}
                    disabled={busy}
                    className="btn-gold w-full !text-sm !py-2 mt-1 disabled:opacity-50"
                    data-testid="crm-welcome-video-generate-btn"
                >
                    {busy
                        ? <Loader2 size={14} className="animate-spin" />
                        : <PlayCircle size={14} />}
                    {isAr ? "أنشئ الفيديو الآن" : "צור וידאו עכשיו"}
                </button>
            )}
        </div>
    );
}

function TagsEditor({ lang, allTags, tags, onChange }) {
    const [input, setInput] = React.useState("");
    const isAr = lang === "ar";
    const suggestions = React.useMemo(() => {
        const base = new Set([...SUGGESTED_TAGS_HE, ...(allTags || [])]);
        // Filter out already-attached
        tags.forEach((t) => base.delete(t));
        return Array.from(base).slice(0, 8);
    }, [allTags, tags]);

    const addTag = (raw) => {
        const t = (raw || "").trim();
        if (!t) return;
        if (tags.includes(t)) return;
        onChange([...tags, t]);
        setInput("");
    };
    const removeTag = (t) => onChange(tags.filter((x) => x !== t));

    return (
        <div data-testid="crm-tags-editor">
            <label className="text-xs uppercase tracking-wider font-bold text-[#1A365D] block mb-1.5">
                {isAr ? "التاجات" : "תגיות"}
            </label>
            <div className="flex flex-wrap gap-1.5 mb-2">
                {tags.length === 0 && (
                    <span className="text-xs text-[#475569]">{isAr ? "بدون تاجات" : "אין תגיות"}</span>
                )}
                {tags.map((t) => (
                    <span key={t} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-[#D4AF37]/15 text-[#8B6F1E] border border-[#D4AF37]/40">
                        <Tag size={10} />{t}
                        <button
                            onClick={() => removeTag(t)}
                            className="hover:text-red-600 ms-0.5"
                            data-testid={`crm-tag-remove-${t}`}
                            aria-label="remove"
                        >
                            <X size={10} />
                        </button>
                    </span>
                ))}
            </div>
            <div className="flex items-center gap-1.5">
                <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(input); } }}
                    placeholder={isAr ? "أضف تاج جديد..." : "הוסף תגית חדשה..."}
                    className="input-royal !py-1.5 flex-1 text-sm"
                    data-testid="crm-tag-input"
                />
                <button
                    onClick={() => addTag(input)}
                    disabled={!input.trim()}
                    className="btn-royal !py-1.5 !px-2.5 disabled:opacity-40"
                    data-testid="crm-tag-add-btn"
                    aria-label="add"
                >
                    <Plus size={14} />
                </button>
            </div>
            {suggestions.length > 0 && (
                <div className="mt-2">
                    <div className="text-[10px] text-[#475569] mb-1">{isAr ? "اقتراحات:" : "הצעות:"}</div>
                    <div className="flex flex-wrap gap-1">
                        {suggestions.map((s) => (
                            <button
                                key={s}
                                onClick={() => addTag(s)}
                                className="text-[10px] px-2 py-0.5 rounded-full border border-[#E2E8F0] text-[#475569] hover:bg-[#F1F5F9] hover:border-[#D4AF37]"
                                data-testid={`crm-tag-suggest-${s}`}
                            >
                                + {s}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
